import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO/SEO';

const Homepage: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "ERHR Global - HR Consulting Boston MA | Strategic HR Solutions",
    "description": "Boston-based HR consulting firm providing strategic HR solutions, transitional HR services, employee engagement, and performance management for businesses in Massachusetts.",
    "url": "https://erhrglobal.com/",
    "mainEntity": {
      "@type": ["Organization", "ProfessionalService"],
      "name": "ERHR Global",
      "url": "https://erhrglobal.com",
      "logo": "https://erhrglobal.com/logo192.webp",
      "image": "https://erhrglobal.com/logo192.webp",
      "description": "Boston-based HR consulting firm specializing in transitional HR services and strategic HR consulting for businesses in Massachusetts and beyond.",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": "contact@erhrglobal.com",
        "url": "https://erhrglobal.com/contact",
        "availableLanguage": "English"
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Boston",
        "addressRegion": "MA",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 42.3601,
        "longitude": -71.0589
      },
      "areaServed": [
        {"@type": "City", "name": "Boston"},
        {"@type": "State", "name": "Massachusetts"}
      ],
      "serviceType": ["HR Consulting", "Transitional HR", "Strategic HR Planning", "Employee Engagement", "Performance Management", "Benefits Strategy", "HR Systems Implementation", "Interim HR", "Fractional HR"],
      "sameAs": [
        "https://www.linkedin.com/company/erhr-global"
      ],
      "foundingDate": "2024",
      "industry": "Human Resources Consulting",
      "priceRange": "$$",
      "knowsAbout": ["Human Resources", "Strategic Planning", "Employee Engagement", "Performance Management", "Transitional HR", "Benefits Strategy", "HR Systems", "Organizational Development", "Massachusetts Employment Law", "Boston Business Consulting"]
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What HR consulting services does ERHR Global offer in Boston?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ERHR Global offers comprehensive HR consulting services in Boston, Massachusetts including transitional HR support, strategic HR planning, employee engagement programs, performance management design, HR systems implementation, benefits strategy, and company policies development."
        }
      },
      {
        "@type": "Question",
        "name": "What is transitional HR and how can it help my Boston business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Transitional HR provides immediate, hands-on HR support during periods of disruption such as unexpected departures, rapid growth, or restructuring. ERHR Global steps in fast to maintain HR operations continuity for businesses in Boston and throughout Massachusetts."
        }
      },
      {
        "@type": "Question",
        "name": "Does ERHR Global offer fractional or interim HR services in Massachusetts?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, ERHR Global provides fractional and interim HR leadership services for businesses in Boston and Massachusetts. Our experienced HR professionals can step into interim roles, manage day-to-day operations, and provide strategic guidance during transitions."
        }
      },
      {
        "@type": "Question",
        "name": "How quickly can ERHR Global respond to an HR emergency in Boston?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ERHR Global offers rapid response transitional HR services. When HR disruption strikes your Boston-area business, we can step in quickly with experienced professionals who assume full responsibility from day one."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="ERHR Global | HR Consulting Boston MA"
        description="Boston HR consulting firm. Transitional HR, strategic planning, employee engagement & performance management for Massachusetts businesses. Contact us!"
        url="https://erhrglobal.com/"
        type="website"
        structuredData={structuredData}
        additionalStructuredData={[faqStructuredData]}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{backgroundImage: "url('https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=70')"}}></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight mb-6">
                Strategic & Transitional
                <span className="block text-primary-200 mt-3">HR Consulting Services</span>
              </h1>
              <p className="text-xl lg:text-2xl mb-8 text-primary-100">
                Your Trusted Partner for HR Solutions — Contact Us Today
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-white text-primary-700 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors duration-300 text-center"
                >
                  Get Started Today
                </Link>
                <Link
                  to="/schedule-appointment"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary-700 transition-colors duration-300 text-center"
                >
                  Schedule Appointment
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl mb-8">
                <img 
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=75" 
                  srcSet="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=75 400w, https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=75 600w, https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=75 800w"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  alt="Professional team meeting" 
                  className="w-full h-64 object-cover"
                  width={600}
                  height={256}
                  loading="eager"
                  fetchPriority="high"
                />
                <div className="absolute inset-0 bg-primary-600 bg-opacity-80"></div>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-8 border border-white border-opacity-20">
                <h3 className="text-2xl font-semibold mb-4">Why Choose ERHR?</h3>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <svg className="w-6 h-6 text-primary-200 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Expert HR Strategy & Implementation
                  </li>
                  <li className="flex items-center">
                    <svg className="w-6 h-6 text-primary-200 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Transitional & Ongoing Support
                  </li>
                  <li className="flex items-center">
                    <svg className="w-6 h-6 text-primary-200 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Customized Solutions for Your Business
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Animated background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              x: [0, 100, 0],
              y: [0, -50, 0],
            }}
            transition={{ 
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary-400 rounded-full opacity-10 blur-3xl"
          />
          <motion.div
            animate={{ 
              x: [0, -100, 0],
              y: [0, 50, 0],
            }}
            transition={{ 
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-300 rounded-full opacity-10 blur-3xl"
          />
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Strategic HR Services & Solutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive HR solutions designed to bridge gaps and elevate your organization's human resources capabilities
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden"
            >
              <div className="relative mb-6">
                <img 
                  src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=75" 
                  srcSet="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=75 400w, https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=75 600w, https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=75 800w"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  alt="Business transition and growth" 
                  className="w-full h-48 object-cover rounded-lg"
                  width={600}
                  height={192}
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 w-16 h-16 bg-white bg-opacity-90 rounded-xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Transitional HR Services</h3>
              <p className="text-gray-600 mb-4">
              When your organization faces sudden HR disruption — from unexpected departures to rapid growth or internal restructuring — ERHR steps in fast, seamlessly, and with impact.
              </p>
              <p className="text-gray-600 mb-6">
              Our transitional HR professionals in Boston provide interim HR leadership, day-to-day operations management, compliance oversight, and change management support. We assume full responsibility from day one so your business maintains continuity while building toward long-term HR stability.
              </p>
              <Link
                to="/transitional-hr"
                className="text-primary-600 font-semibold hover:text-primary-700 transition-colors duration-200"
              >
                Learn More →
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden"
            >
              <div className="relative mb-6">
                <img 
                  src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=75" 
                  srcSet="https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=75 400w, https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=75 600w, https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=75 800w"
                  sizes="(max-width: 768px) 100vw, 50vw"
                  alt="Strategic planning and analytics" 
                  className="w-full h-48 object-cover rounded-lg"
                  width={600}
                  height={192}
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 w-16 h-16 bg-white bg-opacity-90 rounded-xl flex items-center justify-center">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Strategic HR Consulting</h3>
              <p className="text-gray-600 mb-4">
                Strategic HR consulting across the full employee lifecycle, including workforce planning, talent management, compliance, and organizational development.
              </p>
              <p className="text-gray-600 mb-6">
                From designing employee engagement programs and performance management systems to implementing HR technology and crafting competitive benefits packages, our Boston-based consultants deliver tailored strategies that align with your business goals and drive measurable results.
              </p>
              <div className="space-y-2">
                <Link to="/strategic-hr/employee-engagement" className="block text-primary-600 font-semibold hover:text-primary-700 transition-colors">
                  Employee Engagement →
                </Link>
                <Link to="/strategic-hr/performance-management" className="block text-primary-600 font-semibold hover:text-primary-700 transition-colors">
                  Performance Management →
                </Link>
                <Link to="/strategic-hr/hr-systems" className="block text-primary-600 font-semibold hover:text-primary-700 transition-colors">
                  HR Systems Implementation →
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose ERHR Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose ERHR Global?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We understand that every organization faces unique HR challenges. Based in Boston, Massachusetts, our experienced HR consultants provide tailored solutions that drive real results for businesses of all sizes across a wide range of industries.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Rapid Response</h3>
              <p className="text-gray-600 mb-4">
                When HR disruption strikes, we respond quickly with experienced professionals who can step in immediately and maintain business continuity. Whether it's an unexpected leadership vacancy or a compliance crisis, our team is ready to act within days, not weeks.
              </p>
              <Link to="/transitional-hr" className="text-primary-600 font-medium hover:text-primary-700 transition-colors">
                Learn about Transitional HR →
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Proven Expertise</h3>
              <p className="text-gray-600 mb-4">
                Our team brings decades of combined HR experience across industries, ensuring you receive strategic guidance backed by real-world success. From startups to established enterprises in the Boston area, we have helped organizations build stronger HR foundations and achieve lasting improvements.
              </p>
              <Link to="/who-we-are" className="text-primary-600 font-medium hover:text-primary-700 transition-colors">
                Meet Our Team →
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Customized Solutions</h3>
              <p className="text-gray-600 mb-4">
                We don't believe in one-size-fits-all approaches. Every engagement is tailored to your specific organizational needs, culture, and growth objectives. Our consultants take the time to understand your business before recommending solutions that fit your budget and timeline.
              </p>
              <Link to="/contact" className="text-primary-600 font-medium hover:text-primary-700 transition-colors">
                Get Custom Solution →
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our HR consulting expertise spans across multiple industries, helping organizations of all sizes achieve their human resources objectives.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              "Technology", "Healthcare", "Manufacturing", "Financial Services",
              "Retail", "Non-Profit", "Professional Services", "Education"
            ].map((industry, index) => (
              <motion.div
                key={industry}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg p-6 text-center shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <h3 className="font-semibold text-gray-900">{industry}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-4">Ready for Transitional or Strategic HR Solutions?</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
                Contact us today to discuss how our services can help transform your human resources strategy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-primary-700 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors duration-300"
              >
                Contact Us
              </Link>
              <a
                href={`mailto:${['contact', 'erhrglobal.com'].join('@')}`}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary-700 transition-colors duration-300"
              >
                {['contact', 'erhrglobal.com'].join('@')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
